import {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    lazy,
    Suspense
} from "react"
import { useDispatch } from "react-redux"
import type { PrelabelResponse } from "resha"
import { LabelSource, UniversalSchemaType } from "universal-schema/lib/0.0.4"
import { Loader, InputOutputData } from "dash-styled"
import { getJobResponse } from "../../../../../../../../Actions/API/Job/JobActions"
import { isValidObject } from "../../../../../../../../Helpers/Functions/object"
import { useAsyncCommand } from "../../../../../../../../Hooks/useAsyncCommand"
import { APIAppInstanceModel } from "../../../../../../../../Models/API/AppInstanceModel"
import {
    APIJobModel,
    JobStatus,
    JobType,
    JobVersionItem
} from "../../../../../../../../Models/API/JobModel"
import { findAnnotationOutput } from "../../../../../../../../Models/Client/AnnotationOutputSchemaItem"
import { PartialBy } from "../../../../../../../../Models/Utility/PartialBy"
import { MainThunkDispatch } from "../../../../../../../../Reducers"
import { CodeBlock } from "../../../../../Common/CodeBlock/CodeBlock"
import styles from "./JobDetailInputOutput.module.css"
import { ActionButton } from "../../../../../../../Common/ActionButton/ActionButton"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../../../../Models/Client/ProjectRole"
import {
    AnnotationOutputSchemaItem,
    outputTypeFields,
    SchemaForm
} from "universal-schema/lib"
import { AnnotationViewer } from "../../../../../../../Common/Lazy/AnnotationViewer"
import { InputFormRender } from "../../../../../../../Common/InputFormRender/InputFormRender"
import { possibleOutputFormats } from "../../../Common/constants"
import { useJobPostProcessedData } from "../../../../../../../../Hooks/useJobPostProcessedData"
import { PostProcessedOutput } from "../../../../../../../../Models/DataProgram/TemplateMetadata"
import {
    APIAppAsyncOperationModel,
    getAsyncOperationDisplayText
} from "../../../../../../../../Models/API/APIAppAsyncOperationModel"

const ShortcutManagerContainer = lazy(async () => {
    return import(
        "../../../../../../../Common/Lazy/ShortcutManagerContainer"
    ).then(shortcutContainer => ({
        default: shortcutContainer.ShortcutManagerContainer
    }))
})

interface JobDetailInputOutputProps {
    job: APIJobModel
    instance: APIAppInstanceModel
    inputSchema: SchemaForm
    outputSchema: SchemaForm
    selectedVersion: JobVersionItem
    outputFormatType: typeof possibleOutputFormats[number]
    role: ProjectRole[]
    showOldVersion: boolean
    prelabels: PrelabelResponse
    postProcessedData: PostProcessedOutput
    postProcessTitle?: string
    postProcessOperation?: APIAppAsyncOperationModel
    onEnqueue?: () => Promise<void>
    onRelabel?: () => Promise<void>
    onOperationRetry?: () => Promise<void>
    handleCancel?: () => Promise<void>
    urlSigner: (url: string) => Promise<string>
    onRequestTaskDetail?: (taskId: number) => void
}

/**@deprecated */
export const JobDetailInputOutput: FC<JobDetailInputOutputProps> = ({
    job,
    instance,
    inputSchema,
    outputSchema,
    selectedVersion,
    outputFormatType,
    role,
    showOldVersion,
    prelabels,
    postProcessedData,
    postProcessTitle,
    postProcessOperation,
    onEnqueue,
    onRelabel,
    handleCancel,
    urlSigner,
    onRequestTaskDetail,
    onOperationRetry
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const [measuredHeight, setMeasuredHeight] = useState<number>()
    const [inputMeasuredHeight, setInputMeasuredHeight] = useState<number>()
    const [outputMeasuredHeight, setOutputMeasuredHeight] = useState<number>()
    const dispatch = useDispatch<MainThunkDispatch>()
    const refresh = useAsyncCommand(() =>
        onOperationRetry !== undefined
            ? onOperationRetry()
            : dispatch(getJobResponse(job.id))
    )
    const { formData: postProcessedOutput, schema: postProcessedSchema } =
        useJobPostProcessedData(
            inputSchema,
            selectedVersion.responseData,
            postProcessTitle
        )

    const annotationItem = useMemo(
        () =>
            findAnnotationOutput(
                outputSchema,
                selectedVersion.status,
                selectedVersion.response
            ),
        [outputSchema, selectedVersion]
    )

    const renderOnlyOutput = useMemo(() => {
        const uiField = outputSchema.uiSchema["ui:field"] as any
        return (
            selectedVersion?.status === JobStatus.COMPLETED &&
            selectedVersion?.response !== undefined &&
            outputTypeFields.includes(uiField)
        )
    }, [outputSchema, selectedVersion])

    const layout = useMemo(
        () =>
            outputFormatType !== "json" &&
            annotationItem !== undefined &&
            annotationItem.type !== UniversalSchemaType.TOKEN_ANNOTATION
                ? "fit-container"
                : "stacked",
        [annotationItem, outputFormatType]
    )
    const isCollaboratorJob = useMemo(
        () => job.jobType === JobType.COLLABORATOR,
        [job.jobType]
    )

    const handleInputOutputLayoutChange = useCallback(
        (
            input: { height: number; width: number },
            output: { height: number; width: number }
        ) => {
            setInputMeasuredHeight(input.height - 60)
            setOutputMeasuredHeight(output.height - 60)
        },
        []
    )

    const handleEnqueue = useAsyncCommand(onEnqueue)
    const handleRelabel = useAsyncCommand(onRelabel)
    const handleCancelAsync = useAsyncCommand(handleCancel)

    const renderInput = (format: "json" | "formatted" | "processed") => {
        if (format === "json") {
            return <CodeBlock className="h-full">{job.input}</CodeBlock>
        }

        return isValidObject(inputSchema) && isValidObject(job.input) ? (
            <InputFormRender
                {...inputSchema}
                formData={job.input}
                skipMissingDataField={false}
                documentMaxHeight={
                    inputMeasuredHeight ? `${inputMeasuredHeight}px` : undefined
                }
            />
        ) : (
            <>Unknown input</>
        )
    }

    const renderOutputNotReady = (text?: string, buttonText = "Refresh") => (
        <ActionButton
            text={
                text === undefined || text === ""
                    ? "Output isn’t here quite yet"
                    : text
            }
            action={refresh.perform}
            buttonText={
                refresh.isProcessing ? `${buttonText}ing...` : buttonText
            }
            disabled={refresh.perform === undefined || refresh.isProcessing}
        />
    )

    const isInstanceDefined = useCallback(
        (
            item: PartialBy<AnnotationOutputSchemaItem, "schema_instance">
        ): item is AnnotationOutputSchemaItem => {
            return item.schema_instance !== undefined
        },
        []
    )
    const handleRequestLabelSourceDetail = useCallback(
        (source: LabelSource) => {
            if (source.type === "task" && onRequestTaskDetail !== undefined) {
                onRequestTaskDetail(source.id)
            }
        },
        [onRequestTaskDetail]
    )

    const renderAnnotation = (
        annotationItem: PartialBy<AnnotationOutputSchemaItem, "schema_instance">
    ) => {
        if (isInstanceDefined(annotationItem)) {
            return (
                <div
                    className={
                        outputFormatType === "formatted" ? "h-full" : "hidden"
                    }
                >
                    <AnnotationViewer
                        mode="review"
                        showVideoFramePane={true}
                        key={selectedVersion.versionName}
                        appParams={instance.appParams}
                        output={annotationItem}
                        urlSigner={urlSigner}
                        prelabels={prelabels}
                        prelabelsInitialVisibility={
                            job.status !== JobStatus.COMPLETED
                        }
                        onRequestLabelSourceDetail={
                            handleRequestLabelSourceDetail
                        }
                        fallback={
                            <Loader size="xxxl" absolute={true} mask={false} />
                        }
                        enableShortcuts={false}
                    />
                </div>
            )
        }

        return renderOutputNotReady()
    }

    const renderOutput = (
        format: "json" | "formatted" | "processed"
    ): JSX.Element => {
        switch (selectedVersion?.status) {
            case JobStatus.COMPLETED:
                if (selectedVersion.response === undefined) {
                    return renderOutputNotReady()
                }

                if (
                    format === "processed" &&
                    (postProcessedOutput === undefined ||
                        postProcessedOutput === "")
                ) {
                    return renderOutputNotReady(
                        postProcessOperation === undefined
                            ? undefined
                            : getAsyncOperationDisplayText(
                                  postProcessOperation.status === "FAILED"
                                      ? "Failed to process output of data point"
                                      : postProcessOperation.status ===
                                            "IN_PROGRESS" ||
                                        postProcessOperation.status ===
                                            "SCHEDULED"
                                      ? "Waiting for processed output"
                                      : "",
                                  postProcessOperation
                              ),
                        postProcessOperation?.status === "FAILED"
                            ? "Retry"
                            : undefined
                    )
                }

                if (format === "json") {
                    return (
                        <CodeBlock className="h-full" updateOnChange={true}>
                            {selectedVersion.response}
                        </CodeBlock>
                    )
                }

                if (format === "processed") {
                    return !selectedVersion.responseData ? (
                        <ActionButton
                            text="Output isn’t here quite yet"
                            action={refresh.perform}
                            buttonText={
                                refresh.isProcessing
                                    ? "Refreshing..."
                                    : "Refresh"
                            }
                            disabled={
                                refresh.perform === undefined ||
                                refresh.isProcessing
                            }
                        />
                    ) : (
                        <InputFormRender
                            key={`${job.uuid}_output_post_processed_${selectedVersion.versionName}`}
                            className="h-full"
                            formData={postProcessedOutput}
                            {...postProcessedSchema}
                            skipMissingDataField={false}
                            documentMaxHeight={
                                outputMeasuredHeight
                                    ? `${outputMeasuredHeight}px`
                                    : undefined
                            }
                        />
                    )
                }
                return !selectedVersion.response ? (
                    <ActionButton
                        text="Output isn’t here quite yet"
                        action={refresh.perform}
                        buttonText={
                            refresh.isProcessing ? "Refreshing..." : "Refresh"
                        }
                        disabled={
                            refresh.perform === undefined ||
                            refresh.isProcessing
                        }
                    />
                ) : (
                    <InputFormRender
                        key={`${job.uuid}_output_${selectedVersion.versionName}`}
                        className="h-full"
                        formData={selectedVersion.response}
                        {...outputSchema}
                        prelabels={prelabels}
                        skipMissingDataField={false}
                        documentMaxHeight={
                            outputMeasuredHeight
                                ? `${outputMeasuredHeight}px`
                                : undefined
                        }
                    />
                )
            case JobStatus.PENDING:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:submit") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${showOldVersion ? "was" : "is"} parked`}
                        action={handleEnqueue.perform}
                        buttonText={
                            handleEnqueue.isProcessing
                                ? "Queuing..."
                                : "Queue for processing"
                        }
                        disabled={
                            handleEnqueue.perform === undefined ||
                            handleEnqueue.isProcessing
                        }
                    />
                )
            case JobStatus.CANCELED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:relabel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${
                            showOldVersion ? "was" : "has been"
                        } canceled`}
                        action={handleRelabel.perform}
                        buttonText={
                            handleRelabel.isProcessing
                                ? "Sending for reprocess..."
                                : "Send for reprocessing"
                        }
                        disabled={
                            handleRelabel.perform === undefined ||
                            handleRelabel.isProcessing
                        }
                    />
                )
            case JobStatus.EXPIRED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:relabel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point is expired`}
                        action={handleRelabel.perform}
                        buttonText={
                            handleRelabel.isProcessing
                                ? "Sending for reprocess..."
                                : "Send for reprocessing"
                        }
                        disabled={
                            handleRelabel.perform === undefined ||
                            handleRelabel.isProcessing
                        }
                    />
                )
            case JobStatus.FAILED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:relabel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point is failed`}
                        action={handleRelabel.perform}
                        buttonText={
                            handleRelabel.isProcessing
                                ? "Sending for reprocess..."
                                : "Send for reprocessing"
                        }
                        disabled={
                            handleRelabel.perform === undefined ||
                            handleRelabel.isProcessing
                        }
                    />
                )
            case JobStatus.IN_PROGRESS:
            case JobStatus.INTERNAL_ERROR:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:cancel") ||
                            showOldVersion
                        }
                        text={`This data point is ${
                            isCollaboratorJob
                                ? "ready to process"
                                : "processing"
                        }`}
                        action={handleCancelAsync.perform}
                        buttonText={
                            handleCancelAsync.isProcessing
                                ? "Canceling..."
                                : "Cancel"
                        }
                        disabled={
                            handleCancelAsync.perform === undefined ||
                            handleCancelAsync.isProcessing
                        }
                    />
                )
            case JobStatus.SCHEDULED:
            case JobStatus.ENQUEUED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:cancel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${showOldVersion ? "was" : "is"} queued`}
                        action={handleCancelAsync.perform}
                        buttonText={
                            handleCancelAsync.isProcessing
                                ? "Canceling..."
                                : "Cancel"
                        }
                        disabled={
                            handleCancelAsync.perform === undefined ||
                            handleCancelAsync.isProcessing
                        }
                    />
                )
            case JobStatus.SUSPENDED:
                return (
                    <ActionButton
                        hideButton={showOldVersion}
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${showOldVersion ? "was" : "is"} paused`}
                    />
                )
            case JobStatus.IN_CREATION:
                return (
                    <div
                        className={`flex flex-col text-center ${styles.InCreationOutput}`}
                    >
                        <Loader size="xxxl" />
                        <p
                            className={`flex-grow text-smoke-850 ${styles.InCreationOutputText}`}
                            data-testid="in-creation-output-label"
                        >
                            This version is being created
                        </p>
                    </div>
                )
        }
    }

    useEffect(() => {
        setMeasuredHeight(ref.current?.offsetHeight)
    }, [])

    return (
        <Suspense fallback={<Loader size="xxxl" />}>
            <ShortcutManagerContainer>
                <div
                    ref={ref}
                    data-testid="job-detail-input-output"
                    className={`relative flex-grow pb-4 ${
                        layout === "fit-container"
                            ? "flex-shrink h-0"
                            : "flex-shrink-0"
                    }`}
                    style={{
                        minHeight:
                            layout === "stacked" ? undefined : measuredHeight
                    }}
                >
                    {annotationItem === undefined ? (
                        <InputOutputData
                            inputTestId="input-data-block"
                            className={`${renderOnlyOutput ? "px-15%" : ""} ${
                                outputFormatType === "formatted"
                                    ? "h-full"
                                    : "hidden"
                            }`}
                            inputs={renderInput("formatted")}
                            outputs={renderOutput("formatted")}
                            fitToContainerHeight={
                                postProcessedData === "available"
                            }
                            card={outputFormatType !== "formatted"}
                            onlyOutput={renderOnlyOutput}
                            onLayoutChange={
                                outputFormatType === "formatted"
                                    ? handleInputOutputLayoutChange
                                    : undefined
                            }
                        />
                    ) : (
                        renderAnnotation(annotationItem)
                    )}
                    <InputOutputData
                        className={
                            outputFormatType === "json" ? "h-full" : "hidden"
                        }
                        inputs={renderInput("json")}
                        outputs={renderOutput("json")}
                        fitToContainerHeight={true}
                        card={outputFormatType !== "json"}
                    />
                    {postProcessedData === "available" ? (
                        <InputOutputData
                            className={
                                outputFormatType === "processed"
                                    ? "h-full"
                                    : "hidden"
                            }
                            inputs={renderInput("processed")}
                            outputs={renderOutput("processed")}
                            fitToContainerHeight={true}
                            onLayoutChange={
                                outputFormatType === "processed"
                                    ? handleInputOutputLayoutChange
                                    : undefined
                            }
                        />
                    ) : null}
                </div>
            </ShortcutManagerContainer>
        </Suspense>
    )
}
